<template>
    <div id="voiceRecorder">
        <div class="trigger-button" @click="handleButtonClick">
            <slot />
        </div>
        
        <!-- Kayıt Fazı -->
        <div class="voice-record-phase" v-if="isRecording && !isPreview">
            <a-tooltip :title="$t('message.cancelTooltip')">
                <md-button class="actionBtn" @click="cancelRecord" id="cancelBtn">
                    <md-icon class="icon">close</md-icon>
                </md-button>
            </a-tooltip>
            
            <div class="frequence">
                <av-media
                    id="av-media"
                    :media="media"
                    :type="'frequ'"
                    :canvWidth="90"
                    :canvHeight="24"
                    lineColor="white"
                    :frequ-lnum="15"
                ></av-media>
            </div>
            
            <div class="duration-display">
                {{ formattedDuration }} / {{ formattedMaxDuration }}
            </div>
        </div>

        <!-- Önizleme Fazı -->
        <div class="voice-preview-phase" v-if="isPreview">
            <a-tooltip :title="$t('message.deleteAndDiscardTooltip')">
                <md-button class="actionBtn" @click="cancelPreview" id="deleteBtn">
                    <md-icon class="icon">delete_outline</md-icon>
                </md-button>
            </a-tooltip>
            
            <div class="preview-player-container">
                <a-tooltip :title="previewPaused ? 'Oynat' : 'Duraklat'">
                    <div @click="togglePreviewAudio()">
                        <md-icon id="playButton">{{ previewPaused ? "play_arrow" : "pause" }}</md-icon>
                    </div>
                </a-tooltip>
                
                <av-waveform
                    ref="waveformPreview"
                    id="waveform-preview"
                    :playtime="false"
                    :playtimeWithMs="false"
                    :playtimeTextBottom="false"
                    :playtimeSliderWidth="2"
                    :noplayedLineColor="'#999999'"
                    :played-line-color="'#4e8cff'"
                    :audio-src="previewUrl"
                    :playtimeFontSize="0"
                    :canvWidth="140"
                    :canvHeight="30"
                    :played-line-width="0.5"
                    :noplayed-line-width="0.5"
                    :playtime-slider-color="'#4E8CFF'"
                ></av-waveform>
            </div>
            
            <div class="duration-display preview-duration">
                {{ formattedDuration }}
            </div>
        </div>
    </div>
</template>

<script>
import { getRecorder } from "./util/voiceRecorder";
import log2 from "../utils/log2";

const log = log2("VoiceRecorder");

export default {
    name: "VoiceRecorder",
    props: ["cb", "canvW", "canvH"],
    data() {
        return {
            isRecording: false,
            isPreview: false,
            previewPaused: true,
            duration: 0,
            interval: null,
            maxSec: 60 * 5, // 5 dakika
            media: null,
            savedRecording: null,
            previewUrl: null,
        };
    },
    computed: {
        formattedDuration() {
            const minutes = Math.floor(this.duration / 60);
            const seconds = this.duration % 60;
            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        },
        formattedMaxDuration() {
            const minutes = Math.floor(this.maxSec / 60);
            const seconds = this.maxSec % 60;
            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }
    },
    methods: {
        handleButtonClick() {
            if (this.isRecording) {
                // Kayıt devam ediyor, durdur ve önizlemeye geç
                this.finishRecording();
            } else if (this.isPreview) {
                // Önizleme modunda, gönder
                this.sendRecording();
            } else {
                // Kayıt yok, başlat
                this.$feature.beforeSendFileMessage(this.record);
            }
        },
        record() {
            this.duration = 0;
            this.isPreview = false;
            getRecorder().start((e) => {
                this.media = e.target.stream;
            });
            this.isRecording = true;
            this.interval = setInterval(() => this.duration++, 1000);
        },
        finishRecording() {
            if (!this.isRecording) return;
            
            if (this.interval !== null) {
                clearInterval(this.interval);
            }
            
            getRecorder().stop((recording) => {
                this.savedRecording = recording;
                this.previewUrl = URL.createObjectURL(recording);
                this.isRecording = false;
                this.isPreview = true;
                this.previewPaused = true;
                this.interval = null;
            });
        },
        togglePreviewAudio() {
            const audio = this.$refs.waveformPreview.$el.querySelector('audio');
            if (audio) {
                audio[audio.paused ? 'play' : 'pause']();
                this.previewPaused = !this.previewPaused;
            }
        },
        sendRecording() {
            if (this.savedRecording && this.cb) {
                this.cb(this.savedRecording);
            }
            this.cleanupPreview();
        },
        cancelRecord() {
            if (!this.isRecording) return;
            
            if (this.interval !== null) {
                clearInterval(this.interval);
            }
            
            getRecorder().stop(() => {});
            this.isRecording = false;
            this.interval = null;
            this.duration = 0;
        },
        cancelPreview() {
            this.cleanupPreview();
        },
        cleanupPreview() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
            }
            this.isPreview = false;
            this.savedRecording = null;
            this.previewUrl = null;
            this.duration = 0;
        },
        emitState() {
            // State gönderimleri: idle, recording, preview
            if (this.isRecording) {
                this.$emit("recording", true);
                this.$emit("state", "recording");
            } else if (this.isPreview) {
                this.$emit("recording", true);
                this.$emit("state", "preview");
            } else {
                this.$emit("recording", false);
                this.$emit("state", "idle");
            }
        },
    },
    watch: {
        duration(newVal, oldVal) {
            if (newVal >= this.maxSec) {
                // Maksimum süreye ulaşıldı, otomatik olarak kayıt bitir ve önizlemeye geç
                this.finishRecording();
            }
        },
        isRecording(val) {
            this.emitState();
        },
        isPreview(val) {
            this.emitState();
        },
    },
    mounted() {
        this.emitState();
    },
    beforeDestroy() {
        getRecorder().stop(() => {});
        clearInterval(this.interval);
        this.cleanupPreview();
    },
};
</script>

<style lang="scss">
#voiceRecorder {
    position: relative;
    display: flex;
    align-items: center;

    .actionBtn {
        min-width: 24px;
        margin: 0;
    }

    .trigger-button {
        z-index: 3;
    }
    .voice-record-phase,
    .voice-preview-phase {
        z-index: 2;
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 4px;
        background: var(--color-bg-neutral-default);
        border: 1px solid var(--border-neutral-default);
        border-radius: 3rem;
        padding: 4px 45px 4px 8px;
        right: -5px;
        min-width: 300px;

        #cancelBtn {
            .icon {
                color: #fe0000;
                opacity: 0.4;
            }

            &:hover,
            &:active,
            &.md-focused {
                &:before {
                    background-color: transparent;
                }

                .icon {
                    color: #fe0000;
                    opacity: 1;
                    -ms-transform: scale(1.2);
                    -webkit-transform: scale(1.2);
                    transform: scale(1.2);
                    transition: all 0.3s ease;
                }
            }
        }

        #deleteBtn {
            .icon {
                color: #ff5252;
                opacity: 0.6;
                font-size: 22px;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }

            &:hover,
            &:active,
            &.md-focused {
                &:before {
                    background-color: rgba(255, 82, 82, 0.08) !important;
                }

                .icon {
                    color: #ff1744;
                    opacity: 1;
                    -ms-transform: scale(1.15) rotate(-10deg);
                    -webkit-transform: scale(1.15) rotate(-10deg);
                    transform: scale(1.15) rotate(-10deg);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
            }

            &:active {
                .icon {
                    -ms-transform: scale(1.05) rotate(-5deg);
                    -webkit-transform: scale(1.05) rotate(-5deg);
                    transform: scale(1.05) rotate(-5deg);
                }
            }
        }

        .frequence {
            background: rgba(78, 140, 255, 0.8);
            border-radius: 3rem;
            width: max-content;
            padding: 3px 8px;
            display: flex;
            align-items: center;
        }

        .duration-display {
            color: #333333;
            font-size: 11px;
            font-weight: 600;
            white-space: nowrap;
            margin: 0 4px;
            font-family: monospace;
            
            &.preview-duration {
                background: linear-gradient(135deg, #4e8cff 0%, #3b7de0 100%);
                color: white;
                padding: 4px 10px;
                border-radius: 12px;
                font-size: 10px;
                font-weight: 700;
                letter-spacing: 0.5px;
                box-shadow: 0 2px 4px rgba(78, 140, 255, 0.2);
            }
        }

        .preview-player-container {
            display: flex;
            align-items: center;
            gap: 4px;
            
            > div {
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;
                
                &:hover {
                    i.md-icon {
                        transform: scale(1.1);
                        color: #3b7de0;
                    }
                }
                
                &:active {
                    i.md-icon {
                        transform: scale(0.95);
                    }
                }
            }
            
            i.md-icon {
                font-style: normal !important;
                background-color: none !important;
                padding: 0 !important;
                margin: 0 !important;
                color: #4e8cff;
                font-size: 26px;
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            }

            #waveform-preview {
                background: linear-gradient(to bottom, #f8f8f8, #ececec);
                border: 1px solid #e0e0e0;
                border-radius: 20px;
                padding: 4px 10px;
                box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);

                > div:nth-child(1) {
                  width: 0 !important;
                  height: 0 !important;
                }
            }
        }
    }
    
    .voice-preview-phase {
        background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        border: 1px solid #e8e8e8;
    }
}

// Waveform canvas boyut düzeltmesi
#voiceRecorder {
    av-waveform {
        display: flex;
        align-items: center;
        
        canvas {
            display: block;
        }
    }
}
</style>
